/* C4 — PitchPrint bridge for product.c4-legacy.
 *
 * WHAT THIS IS FOR. On the legacy template Apippa prices the job and PitchPrint is the
 * "Design it online" engine. Both are store-level scripts that inject their own UI into
 * whatever theme they land in. This file does four things and deliberately nothing more:
 *
 *   1. Presents PitchPrint through the SAME C4 artwork card the online template uses, so
 *      "Edit Design" / "Clear Design" mean the same thing on both templates. PitchPrint's own
 *      button bar (#pp_main_btn_sec) stays in the DOM -- its buttons are the real actuators we
 *      click -- but is hidden, so the customer never sees two sets of controls.
 *   2. Shows the product's PitchPrint templates as a carousel inside that card, with Customise
 *      on each thumbnail. See DESIGN LIST below for where the list comes from.
 *   3. Puts the saved design's preview into the C4 gallery, via the shared
 *      properties[_c4_design_preview] slot that c4-print-app.js already reads -- NOT as a second
 *      preview strip under the calculator, which is what the Dawn theme did.
 *   4. Keeps PitchPrint's project identity per CART LINE, and clears the product-level slot only
 *      once the design is provably in the cart.
 *
 * WHY 4 MATTERS (the bug this replaces). PitchPrint tracks one project per PRODUCT, in
 * localStorage["pprint-sp"][productId]. The custom JS previously in PitchPrint's app settings
 * cleared that slot on every /cart/add submit, unconditionally -- so "Continue editing" reopened
 * the base template instead of the customer's design (removed 10 Sep 2026). Print.App solves the
 * same problem correctly: it clears only after confirming a cart line carries the project id.
 * This file applies that rule to PitchPrint, and evaluates it on page load rather than in a
 * timer, because Apippa hard-redirects to /cart about a second after add -- any post-click poll
 * on the product page is killed by the navigation.
 *
 * DESIGN LIST. C4's products point PitchPrint at a CATEGORY (product metafield global.pprint,
 * e.g. "*f029be30..."). For a category, PitchPrint's page-side client never receives the design
 * list: the frame reports a "category module" and _designsFetched() leaves #pp_design_select
 * empty (verified 10 Sep 2026). There is therefore no native parent-side source to read, which is
 * why the live Dawn store shipped its own list. The list is read, in order of preference:
 *   a) ppclient._vars.designList / designStack, if PitchPrint ever populates it (single-design
 *      products, and any future build that exposes categories to the parent);
 *   b) assets/pp-designs.json -- the SAME file the live Dawn carousel uses, carried across
 *      unchanged (approved by Cathy, 10 Sep 2026, as the legacy data source). Its URL comes from
 *      data-c4-pp-designs on the form, so nothing about the list is hardcoded in this file;
 *   c) nothing -- and the card falls back to PitchPrint's OWN template gallery, which is what its
 *      Customise button opens. That is still native template selection, just PitchPrint's screen
 *      instead of an inline strip.
 * Records are matched to the product by title (normalised, one trailing "s" dropped) and then
 * narrowed by the customer's live Apippa Size / Shape / Orientation, each narrowing falling back
 * to the records that do not specify that attribute -- the same rules the Dawn carousel used.
 * When the JSON is eventually replaced by a live PitchPrint category endpoint, only readList()
 * changes.
 *
 * EVERYTHING ELSE IS LEFT ALONE. Apippa owns configuration, price, quantity and the add itself;
 * PitchPrint owns the editor and its project store. This file never rewrites a price and never
 * touches a protected variant. Its ONE cart write is the Duplicate link (section 2b): a re-add of
 * an existing line's own variant and properties under a freshly cloned project id.
 */
(function () {
  /* RUN ONCE PER PAGE. This file is requested by the legacy product section AND by the cart
     drawer, which the layout renders on every page -- so without this guard a legacy PDP builds
     two of everything, most visibly two template carousels. One instance serves both surfaces. */
  if (window.__c4PitchPrint) return;
  window.__c4PitchPrint = true;

  var form = document.querySelector('form[data-c4-legacy]');
  var onProduct = !!form;

  var PREVIEW_BASE = 'https://pitchprint.io/previews/';
  var SP = 'pprint-sp';          /* PitchPrint: product id -> current project (url-encoded JSON) */
  var PROJECTS = 'pprint-projects'; /* PitchPrint: project id -> the same record */
  var RESTORE_PARAM = 'pp_project';
  var LINE_PARAM = 'pp_line';     /* which cart line is being edited; two lines can share one project */
  var CLONES = 'c4-pp-clones';    /* ours: clone id -> the working project it was frozen from */

  function store(key) {
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return {};
      var v = JSON.parse(raw);
      return v && typeof v === 'object' ? v : {};
    } catch (e) { return {}; }
  }
  function saveStore(key, obj) {
    try { window.localStorage.setItem(key, JSON.stringify(obj)); } catch (e) {}
  }
  /* PitchPrint stores each record as a url-encoded JSON string. */
  function record(value) {
    try { return JSON.parse(decodeURIComponent(value)); } catch (e) { return null; }
  }
  function projectIdOf(value) {
    var r = record(value);
    return (r && r.projectId) || null;
  }

  function previewFor(projectId, page) {
    return PREVIEW_BASE + encodeURIComponent(projectId) + '_' + (page || 1) + '.jpg';
  }

  /* PitchPrint's Shopify client keeps only the CURRENT project per product: _saveSess() returns
     early for client "sp", so the "pprint-projects" archive it maintains on other platforms is
     never written here. Nothing else remembers a project once its product slot is released --
     which would leave a cart line pointing at a design the page can no longer reopen. So we
     archive the record ourselves, in PitchPrint's own store and format, at the moment we release
     it. (Verified 10 Sep 2026: without this, Edit from the cart fell back to a new design.) */
  function archive(projectId, rec) {
    if (!projectId || !rec) return;
    var projects = store(PROJECTS);
    if (projects[projectId] === rec) return;
    projects[projectId] = rec;
    saveStore(PROJECTS, projects);
  }

  function archiveCurrent() {
    var sp = store(SP);
    Object.keys(sp).forEach(function (productId) {
      archive(projectIdOf(sp[productId]), sp[productId]);
    });
  }

  /* ---------------------------------------------------------------------------------------
     1. CONFIRMED-ADD CLEAR. Runs on every page, product or not: whichever page the customer
     lands on after the add (Apippa sends them to /cart), the rule is the same -- a project that
     is already on a cart line has done its job, so its PRODUCT slot is released and the next
     design on that product starts fresh. The project record itself is kept in "pprint-projects"
     so the cart line can still reopen it, and so PitchPrint can claim it to an account later.
  --------------------------------------------------------------------------------------- */
  function releaseAddedProjects() {
    var sp = store(SP);
    var ids = Object.keys(sp);
    if (!ids.length) return;

    /* Editing a cart line is the one time a project SHOULD stay current even though it is in the
       cart: ?pp_project= means the customer chose that line's design deliberately. Without this
       exemption the restore below and this release fight each other and the editor opens blank. */
    var editing = new URLSearchParams(location.search).get(RESTORE_PARAM);

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var inCart = {};
        var clones = store(CLONES);
        (cart.items || []).forEach(function (item) {
          var p = item.properties && item.properties._pitchprint;
          if (!p) return;
          inCart[p] = true;
          /* FROZEN COPIES (15 Sep 2026): a line normally carries a CLONE of the working project
             (c4-cart-add.js). The working project itself is what the customer should not keep
             saving over once it is represented in the cart, so its clone being there releases it. */
          if (clones[p]) inCart[clones[p]] = true;
        });
        var changed = false;
        ids.forEach(function (productId) {
          var pid = projectIdOf(sp[productId]);
          if (pid && inCart[pid] && pid !== editing) {
            archive(pid, sp[productId]);   /* keep it reopenable from the cart line */
            delete sp[productId];
            changed = true;
          }
        });
        if (!changed) return;
        saveStore(SP, sp);
        /* The in-memory client still holds the released project; blank it so a design started
           now cannot be saved back over the one already in the cart. */
        var c = window.ppclient;
        if (c && c._vars && c._vars.projectId && c._vars.projectId !== editing && inCart[c._vars.projectId]) {
          c._vars.projectId = '';
          c._vars.mode = 'new';
        }
      })
      .catch(function () {});
  }

  /* ---------------------------------------------------------------------------------------
     2. CART-LINE EDIT. A cart line carries its own _pitchprint id, so "Edit design" there links
     back to the product with ?pp_project=<id>. On arrival the record is copied out of
     "pprint-projects" into the product slot before PitchPrint reads it, so the client opens THAT
     line's design rather than whatever was last touched on the product.
  --------------------------------------------------------------------------------------- */
  function restoreRequestedProject() {
    var want = new URLSearchParams(location.search).get(RESTORE_PARAM);
    if (!want) return false;

    var projects = store(PROJECTS);
    var rec = projects[want];
    if (!rec) return false;

    var productId = productIdFromRecord(rec) || (window.ppclient && window.ppclient._vars && window.ppclient._vars.productId);
    if (!productId) return false;

    var sp = store(SP);
    if (projectIdOf(sp[productId]) === want) return false; /* already current */
    sp[productId] = rec;
    saveStore(SP, sp);
    return true; /* caller reloads once so PitchPrint boots on the restored project */
  }
  function productIdFromRecord(value) {
    var r = record(value);
    return (r && r.product && r.product.id) || (r && r.productId) || null;
  }

  /* No re-entry guard is needed: restoreRequestedProject() returns false once the project is
     already the current one, so the reload below can only ever happen once per link. An earlier
     sessionStorage flag did guard it, and wrongly refused to restore the SAME line a second time
     later in the session (found 10 Sep 2026). */
  function handleRestore() {
    if (restoreRequestedProject()) location.reload();
  }

  /* Cart page and drawer: an Edit link on every line that carries a design. Added in JS so the
     cart section and the drawer snippet stay untouched. */
  function decorateCartLines() {
    if (!document.querySelector('.cart-items, cart-items-component, cart-drawer-component, #main-cart-items')) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        (cart.items || []).forEach(function (item) {
          var pid = item.properties && item.properties._pitchprint;
          var row = rowForKey(item.key);
          if (!row || row.querySelector('[data-c4-pp-edit]')) return;
          /* "EDIT" ON EVERY LINE, AND IT IS THE ONLY WAY BACK (Cathy, 15 Sep 2026: "change the words
             Edit design to Edit since they can change the calculator too" and "deactivate any other
             area other than Edit so they know for sure they are editing the cart line"). The cart
             snippet renders the thumbnail and title as plain elements; this link is the one click
             that resumes the line -- calculator values come back via pp_line (c4-legacy.js), the
             design via pp_project when there is one. The line key rides along because two lines
             could hold the same project id (pre-freeze lines); without it, Edit on line three
             restored line one's size and price (Cathy, 14 Sep 2026). */
          /* One tidy row of actions under the price (Cathy, 15 Sep 2026: "Edit, Duplicate and
             Delete buttons to be tidied in cart drawer"): Edit and Duplicate sit together in a
             small flex row; the bin stays in the theme's own quantity cell. */
          var host = row.querySelector('.cart-item__details, .cart-item__name, [class*="details"]') || row;
          var actions = document.createElement('div');
          actions.setAttribute('data-c4-line-actions', '');
          actions.style.cssText = 'display:flex;flex-wrap:wrap;align-items:center;gap:6px 16px;margin-top:8px';
          host.appendChild(actions);

          var a = document.createElement('a');
          a.setAttribute('data-c4-pp-edit', '');
          a.href = item.url.split('?')[0] + '?' +
            (pid ? RESTORE_PARAM + '=' + encodeURIComponent(pid) + '&' : '') +
            LINE_PARAM + '=' + encodeURIComponent(item.key);
          a.textContent = 'Edit';
          a.setAttribute('aria-label', 'Edit ' + (item.product_title || item.title || 'this item'));
          a.style.cssText = LINK_CSS;
          actions.appendChild(a);
          /* THE BIN JOINS THE ROW (Cathy, 16 Sep 2026: "put all 3 -- edit, duplicate and trashcan --
             on the same line", drawer and full cart). It is the theme's own remove button, MOVED
             (not copied) out of the quantity cell, so its on:click binding and behaviour are
             untouched; Horizon resolves the handler from the nearest cart component, which is the
             same ancestor either way. Placed after Duplicate; if there is no Duplicate it simply
             follows Edit. */
          var bin = row.querySelector('.cart-items__remove');
          var placeBin = function () {
            if (!bin || bin.hasAttribute('data-c4-in-row')) return;
            bin.setAttribute('data-c4-in-row', '');
            bin.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;width:auto;min-width:0;min-height:0;height:auto;padding:2px;margin:0 0 0 2px;border:0;background:none;box-shadow:none;color:inherit;cursor:pointer;line-height:0';
            actions.appendChild(bin);
          };
          if (!pid) { placeBin(); return; }

          /* PitchPrint's shopify.js decorates the cart page too: it gives the line's preview image
             id="p_<projectId>" and a click handler that opens ITS editor over the cart (Cathy,
             15 Sep 2026: "it also opens the cart page in the editor"). The thumbnail is our
             "view full size" link; a capture-phase stop on the link keeps the click from ever
             reaching the image's handler, while the link's own navigation still happens. */
          var view = row.querySelector('[data-c4-design-view]');
          if (view && !view.__c4View) {
            view.__c4View = true;
            view.addEventListener('click', function (e) { e.stopPropagation(); }, true);
          }

          /* DUPLICATE (Cathy, 15 Sep 2026: "add a duplicate link in the cart to produce a new
             pitchprint design ID"). See duplicateLine() below: the copy gets its OWN project id,
             so editing one line can never rewrite the other. */
          var d = document.createElement('a');
          d.setAttribute('data-c4-pp-dup', '');
          d.href = '#';
          d.textContent = 'Duplicate';
          d.style.cssText = LINK_CSS;
          d.addEventListener('click', function (e) { e.preventDefault(); duplicateLine(item, d); });
          actions.appendChild(d);
          placeBin();
        });
      })
      .catch(function () {});
  }
  var LINK_CSS = 'display:inline-block;font:700 12px/1.3 system-ui,sans-serif;' +
    'color:#101112;border-bottom:1px solid var(--c4-teal,#04a7a7);text-decoration:none';

  /* ---------------------------------------------------------------------------------------
     2b. DUPLICATE A LINE -- WITH A NEW PROJECT ID.

     A plain re-add of the same properties would put ONE project id on two lines, and PitchPrint
     rewrites one preview/one design per project, so a later edit of either line silently changes
     both (the shared-artwork failure the identity model exists to prevent). So the copy is made
     on PitchPrint's side first: the client's own "Duplicate design" link (client.js,
     _duplicateProject) calls POST {apiBase}clone-project with {values:1, id} plus the session
     token PitchPrint keeps in localStorage["pptoken"], and gets back {newId}. Same call here,
     from the customer's browser, with the same credentials -- no secret key, no proxy, nothing
     store-side. Then:

       1. copy the local project record under the new id so "Edit design" on the copy can reopen
          it (restoreRequestedProject reads "pprint-projects");
       2. work out the copy's preview: previews/<newId>_1.jpg if the clone produced one, else the
          original's preview (identical at this moment; the next edit re-adds the line with a
          fresh preview anyway);
       3. POST /cart/add.js with the ORIGINAL line's variant, quantity and every property, only
          _pitchprint and _c4_design_preview swapped. Different properties => Shopify makes a new
          line rather than merging into the old one;
       4. reload so both cart surfaces re-render from the server (the drawer is reopened after).

     Nothing is removed and nothing is written until the clone has succeeded; a failed clone just
     puts the link back with a short message.
  --------------------------------------------------------------------------------------- */
  function cloneProject(pid) {
    var body = { values: 1, id: pid };
    try { var tok = localStorage.getItem('pptoken'); if (tok) body.token = tok; } catch (e) {}
    return fetch('https://api.pitchprint.io/client/clone-project', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        try { if (j && j.token) localStorage.setItem('pptoken', j.token); } catch (e) {}
        return (j && !j.error && j.newId) ? String(j.newId) : null;
      });
  }
  function copyRecord(oldId, newId) {
    try {
      var projects = store(PROJECTS);
      var rec = record(projects[oldId]);
      if (!rec) return;
      var copy = JSON.parse(JSON.stringify(rec));
      copy.projectId = newId;
      projects[newId] = encodeURIComponent(JSON.stringify(copy));
      saveStore(PROJECTS, projects);
    } catch (e) {}
  }
  function previewFor2(newId, fallback) {
    return new Promise(function (resolve) {
      var url = previewFor(newId, 1);
      var img = new Image();
      var done = false;
      var finish = function (ok) { if (done) return; done = true; resolve(ok ? url : (fallback || '')); };
      img.onload = function () { finish(true); };
      img.onerror = function () { finish(false); };
      setTimeout(function () { finish(false); }, 4000);
      img.src = url + '?c4=' + Date.now();
    });
  }
  function duplicateLine(item, link) {
    if (link.getAttribute('data-busy')) return;
    link.setAttribute('data-busy', '1');
    var label = link.textContent;
    link.textContent = 'Duplicating…';
    var pid = item.properties && item.properties._pitchprint;
    var fail = function (msg) {
      link.textContent = msg || 'Could not duplicate — try again';
      setTimeout(function () { link.textContent = label; link.removeAttribute('data-busy'); }, 3500);
    };
    cloneProject(pid)
      .then(function (newId) {
        if (!newId) { fail(); return; }
        copyRecord(pid, newId);
        noteClone(newId, pid);
        var orig = (item.properties._c4_design_preview || '').split('?')[0];
        return previewFor2(newId, orig).then(function (prev) {
          var props = {};
          Object.keys(item.properties || {}).forEach(function (k) {
            var v = item.properties[k];
            if (v !== null && v !== undefined) props[k] = v;
          });
          props._pitchprint = newId;
          if (prev) props._c4_design_preview = prev;
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: item.variant_id, quantity: item.quantity, properties: props }] })
          }).then(function (r) {
            if (!r.ok) throw new Error('add failed');
            try { if (!/^\/cart\/?$/.test(location.pathname)) sessionStorage.setItem('c4:reopen-drawer', '1'); } catch (e) {}
            location.reload();
          });
        });
      })
      .catch(function () { fail(); });
  }
  function removeProject(pid) {
    var body = { id: pid };
    try { var tok = localStorage.getItem('pptoken'); if (tok) body.token = tok; } catch (e) {}
    return fetch('https://api.pitchprint.io/client/remove-project', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }
  function noteClone(cloneId, originalId) {
    if (!cloneId || !originalId) return;
    var clones = store(CLONES);
    clones[cloneId] = originalId;
    /* keep the map small: 60 most recent is far more than one cart ever holds */
    var keys = Object.keys(clones);
    if (keys.length > 60) keys.slice(0, keys.length - 60).forEach(function (k) { delete clones[k]; });
    saveStore(CLONES, clones);
  }
  function isClone(pid) {
    return !!(pid && store(CLONES)[pid]);
  }

  /* ---------------------------------------------------------------------------------------
     2c. FREEZE ON ADD -- the door c4-cart-add.js uses (Cathy, 15 Sep 2026: "protecting the
     id's after ordering ... so we are effectively locking anything added to cart and ordered").

     PitchPrint keeps ONE file per project and rewrites it on every save, so an order that records
     the customer's working project can be changed after payment by the customer simply reopening
     that design. So a cart line never carries the working project: at add time the project is
     CLONED and the line gets the clone. The customer's own design stays theirs to keep editing
     and reordering; what reaches the order is a copy nobody has a resume link to.

     Edit design from the cart opens the clone (pp_project=<clone>); re-adding clones THAT and
     the old line's clone is deleted once the replacement is confirmed (c4-cart-add.js), so the
     cart holds one copy per line and the backend ends up with one frozen copy per ordered line.
     Deletion is only ever of an id in our clone map -- never the customer's original, never an
     id we did not mint on this device. Every call fails safe: no clone => the add proceeds as
     before; failed delete => a spare project, which is recoverable.
  --------------------------------------------------------------------------------------- */
  window.c4PP = {
    clone: cloneProject,
    remove: removeProject,
    copyRecord: copyRecord,
    previewFor: previewFor2,
    noteClone: noteClone,
    isClone: isClone
  };

  /* After a duplicate from the drawer the page reloads; put the drawer back on screen. */
  function reopenDrawerIfAsked() {
    var asked = false;
    try { asked = sessionStorage.getItem('c4:reopen-drawer') === '1'; if (asked) sessionStorage.removeItem('c4:reopen-drawer'); } catch (e) {}
    if (!asked) return;
    var tries = 0;
    (function attempt() {
      var c = document.querySelector('cart-drawer-component');
      var d = c && c.closest('theme-drawer');
      if (d && typeof d.open === 'function') { try { d.open(); } catch (e) {} return; }
      if (++tries < 20) setTimeout(attempt, 150);
    })();
  }
  function rowForKey(key) {
    var direct = document.querySelector('[data-line-item-key="' + key + '"], [data-cart-item-key="' + key + '"], [data-key="' + key + '"]');
    if (direct) return direct.closest('.cart-item, tr, li') || direct;
    var link = document.querySelector('a[href*="' + key + '"]');
    return link ? link.closest('.cart-item, tr, li') : null;
  }

  /* ------------------------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------------------------
     2d. LIGHTBOX for cart thumbnails (Cathy, 15 Sep 2026: "Can all 4 link thumbnails that are
     zoomed previews from full cart/cart drawer (2+2) open in an overlay pop-out window with an X
     top right corner to close?"). Any [data-c4-lightbox] click -- the design thumbnail and the
     upload thumbnails, cart page and drawer -- opens the image in an overlay instead of leaving
     the page (leaving meant browser Back landed on the product page from the drawer). Closes on
     the X, the backdrop, or Escape. The overlay is appended INSIDE the open <dialog> when the
     click comes from the drawer: a modal dialog sits in the browser's top layer, above any
     z-index, so an overlay on <body> would render underneath it. The link's href stays as the
     no-JS fallback. Capture phase, so PitchPrint's own image click handler never sees it. */
  function openLightbox(src, name, fileUrl, host) {
    closeLightbox();
    var box = document.createElement('div');
    box.setAttribute('data-c4-lightbox-overlay', '');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', name || 'Preview');
    box.style.cssText = 'position:fixed;inset:0;z-index:2147483000;background:rgba(16,17,18,.88);' +
      'display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 20px 20px;box-sizing:border-box';
    var x = document.createElement('button');
    x.type = 'button';
    x.setAttribute('aria-label', 'Close preview');
    x.innerHTML = '&#215;';
    x.style.cssText = 'position:absolute;top:14px;right:14px;width:44px;height:44px;border:0;border-radius:50%;' +
      'background:#fff;color:#101112;font:400 28px/44px system-ui,sans-serif;cursor:pointer;box-shadow:0 6px 18px rgba(0,0,0,.35)';
    var img = document.createElement('img');
    img.src = src;
    img.alt = name || '';
    img.style.cssText = 'max-width:min(1200px,100%);max-height:calc(100vh - 140px);width:auto;height:auto;' +
      'object-fit:contain;background:#fff;border-radius:6px;box-shadow:0 30px 80px rgba(0,0,0,.5)';
    var cap = document.createElement('div');
    cap.style.cssText = 'margin-top:12px;display:flex;flex-wrap:wrap;gap:8px 18px;align-items:center;justify-content:center;' +
      'font:600 13px/1.4 system-ui,sans-serif;color:#fff;text-align:center';
    if (name) { var t = document.createElement('span'); t.textContent = name; cap.appendChild(t); }
    /* No "Open original file" link (Cathy, 15 Sep 2026: "My intention is to keep the customer in
       the cart if they are there"). The preview is the image, its name and the X -- nothing that
       leaves the cart. fileUrl is still passed in case that ever changes. */
    box.appendChild(x); box.appendChild(img); box.appendChild(cap);
    box.addEventListener('click', function (e) {
      if (e.target === box || e.target === x) { e.preventDefault(); closeLightbox(); }
    });
    (host || document.body).appendChild(box);
    x.focus();
  }
  function closeLightbox() {
    var b = document.querySelector('[data-c4-lightbox-overlay]');
    if (b) b.remove();
  }
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('[data-c4-lightbox]');
    if (!a) return;
    var src = a.getAttribute('data-c4-lightbox');
    if (!src) return;
    e.preventDefault();
    e.stopPropagation();
    var host = a.closest('dialog[open]') || document.body;
    openLightbox(src, a.getAttribute('data-c4-lightbox-name') || '', a.getAttribute('data-c4-lightbox-file') || '', host);
  }, true);
  document.addEventListener('keydown', function (e) {
    /* preventDefault too, or the same Escape also closes the drawer underneath */
    if (e.key === 'Escape' && document.querySelector('[data-c4-lightbox-overlay]')) { e.preventDefault(); e.stopPropagation(); closeLightbox(); }
  }, true);

  /* The drawer's rows are re-rendered from the server after every add and quantity change, which
     drops the Edit / Duplicate links. Now that the thumbnail and title are not links, a row without
     them has no way back to the line -- so re-decorate after each cart update, on every page. */
  var redecorateTimer = null;
  function redecorateSoon() {
    clearTimeout(redecorateTimer);
    redecorateTimer = setTimeout(decorateCartLines, 500);
  }
  document.addEventListener('c4:cart-added', redecorateSoon);
  document.addEventListener('shopify:cart:lines-update', redecorateSoon, true);
  document.addEventListener('cart:update', redecorateSoon, true);

  if (!onProduct) {
    handleRestore();
    releaseAddedProjects();
    decorateCartLines();
    reopenDrawerIfAsked();
    return;
  }

  handleRestore();
  archiveCurrent();
  releaseAddedProjects();
  decorateCartLines();   /* the drawer is on this page too */

  /* IN-PAGE ADD (c4-cart-add.js, 14 Sep 2026). The confirmed-add clear above used to run on the
     /cart page the customer landed on; now the add opens the drawer and the page stays. So run
     the same release here, once the line exists -- otherwise a second add on this page would
     carry the SAME project id onto a second line, and one save would rewrite both (the shared-
     artwork failure the identity model exists to prevent). c4-cart-add.js drops ?pp_project=
     first, so the project just added is no longer exempt from release. */
  document.addEventListener('c4:cart-added', function () {
    archiveCurrent();
    releaseAddedProjects();
    setTimeout(function () { try { syncCard(); } catch (e) {} }, 400);
  });

  var card = document.querySelector('[data-c4-design-card]');
  var slot = document.querySelector('[data-c4-design-preview]');
  var launch = card && card.querySelector('[data-c4-design-launch]');
  var remove = card && card.querySelector('[data-c4-design-remove]');
  var browse = card && card.querySelector('[data-c4-design-browse]');
  var radio = card && card.querySelector('input[type="radio"]');
  var titleEl = card && card.querySelector('[data-c4-design-title]');
  var tagEl = card && card.querySelector('[data-c4-design-tag]');

  function pp() { return window.ppclient; }
  function ppBtn(id) { return document.getElementById(id); }
  function currentProject() {
    var c = pp();
    var v = c && c._vars && c._vars.projectId;
    if (v) return v;
    var input = form.querySelector('input[name="properties[_pitchprint]"]');
    return (input && input.value) || '';
  }

  function chooseDesignRoute() {
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (card) card.setAttribute('data-c4-touched', '');
  }

  /* PitchPrint's own controls do the work; the C4 card is the face. Clicking a hidden button is
     deliberate: it keeps PitchPrint's own state machine (mode, project, validation) in charge. */
  function clickPP(id) {
    var b = ppBtn(id);
    if (b) { b.click(); return true; }
    return false;
  }

  /* WHAT THE FRAME LAST DREW, as far as this page can know: a design id, '*' when the editor was
     opened without a template (PitchPrint draws its own category default), or null before the
     editor has ever been opened on this page load. Only used to decide whether the frame needs
     telling to redraw -- see openEditor. */
  var frameShowing = null;

  /* ---------------------------------------------------------------------------------------
     THE FRAME KEEPS THE DESIGN IT ALREADY DREW -- "anyway it seems to be 1 behind on every design
     i click into" (Cathy, 14 Sep 2026). Reproduced in the live editor the same day: pick Winter
     Sale, close, pick Salon Directions, and the canvas still says Winter Sale. It is NOT a race
     and no amount of waiting fixes it -- an earlier 700ms deferral did not, and neither did
     waiting eight seconds. design-selected changes which design the frame HAS SELECTED; the frame
     only draws the selection when its library reloads.

     do-reload IS PITCHPRINT'S OWN ANSWER TO THIS, NOT A POKE AT THEIR INTERNALS. Their Design
     Observables module -- the supported way to drive design choice from page options -- watches
     for the editor being shown with a selection that has moved on and calls fire("do-reload").
     client.js fire() posts anything that is not show-app or close-app straight to the frame, so
     do-reload is a published command travelling a published channel. Confirmed live: with the
     wrong design on screen, one do-reload redrew the right one.

     THE RELOAD HAPPENS WHILE THE FRAME IS STILL HIDDEN. Reloading after the editor is on screen
     works too, and was tried: the customer then watches the wrong design sit there for a second
     and swap itself, which is the complaint rather than the fix. Select, reload, then show costs
     a couple of seconds of waiting and the editor opens on the right design first time. Both
     orders were tested in the live editor on End Sign Flag before this was written.

     TWO RELOADS, NOT ONE, AND WHY THE SECOND IS NOT BELT AND BRACES. postMessage keeps its
     order, so a reload always arrives after the selection -- but selecting starts an asynchronous
     fetch inside the frame, and a reload that lands mid-fetch redraws the design being replaced.
     How long that fetch takes is a property of the category, not of this code: End Sign Flag
     (7 designs) settled inside 200ms, Novelty Cheques (46 designs, cheque-sized artwork) did not,
     and a single reload timed for the first left the second showing the previous cheque -- the
     original bug, still there on one product and gone on another. A second reload a second later
     cannot land mid-fetch for any category that has settled by then, and lands harmlessly on the
     already-correct design when the first one won. Both were verified on Novelty Cheques, the
     slowest category on the store, by switching designs twice in a row.

     ONLY WHEN THE DESIGN ACTUALLY CHANGED. The first open after a page load draws the selection
     correctly by itself, so a reload there would buy a second of delay for nothing. NEVER while a
     project is live: do-reload rebuilds the canvas from the design, and a customer reopening
     saved artwork must get their artwork back, not the blank template.
  --------------------------------------------------------------------------------------- */
  function openEditor(designId) {
    chooseDesignRoute();
    var c = pp();
    /* PitchPrint not up yet: the artwork choice is still registered, and its own button stays the
       way in once it mounts. Nothing is faked and no editor is promised. */
    if (!c) return;
    var show = function () {
      if (!clickPP('pp_customize_design_btn')) {
        try { c.showApp(); } catch (e) {}
      }
    };

    if (designId) {
      var redraw = frameShowing !== null && frameShowing !== designId && !c._vars.projectId;
      frameShowing = designId;
      c._vars.designId = designId;
      try { c._sendMsg('design-selected', null, designId); } catch (e) {}
      if (redraw) {
        var reload = function () { try { c.fire('do-reload'); } catch (e) {} };
        setTimeout(reload, 400);
        setTimeout(reload, 1400);
        setTimeout(show, 2200);
      } else {
        setTimeout(show, 700);
      }
      return;
    }
    frameShowing = '*';
    show();
  }

  /* ---------------------------------------------------------------------------------------
     EDIT DESIGN DOES NOT TRUST PITCHPRINT TO REMEMBER THE PROJECT (14 Sep 2026).
     "Edit Design" used to be nothing but a click on PitchPrint's own #pp_edit_btn, which reads
     the client's IN-MEMORY ppclient._vars.projectId. When that is empty the client is in "new"
     mode and reopens a template instead of the customer's saved artwork -- the design appears to
     have forgotten every edit. THE SAME SYMPTOM IS NOW ON THE LIVE THEME, which carries none of
     this file, so whatever empties _vars.projectId is happening store-side and we cannot wait for
     it to stop or work around it in one place. The only safe assumption is that PitchPrint's
     memory may be blank at the moment the customer clicks, and that we must hand the project back
     before the click rather than hope it is still there.

     THE PAGE ALWAYS KNOWS MORE THAN THE CLIENT DOES. Three sources, in this order:
       1. ppclient._vars.projectId, when it is already set -- a live project is never overwritten.
       2. currentProject(), which falls back to PitchPrint's own hidden
          input[name="properties[_pitchprint]"]. That input survives the blanking, which is why the
          card can still read "Design ready" while the client has forgotten the project entirely.
       3. The session record itself: the client looks up localStorage["pprint-sp"][productId] when
          it opens, so a missing record is rebuilt here from "pprint-projects" -- the archive this
          file writes on every save precisely so a released or forgotten project stays reopenable.
          Identical copy to the one restoreRequestedProject() performs for a cart-line Edit link.
     Then _vars.projectId and _vars.mode are set explicitly, so the click that follows can only be
     read as "reopen THIS project". Returns the id it settled on, or '' when there is none -- and
     with none, nothing is changed and the click behaves exactly as it did before: a new design,
     which is the right answer when there is genuinely no design to edit.

     GUARDED THROUGHOUT. PitchPrint mounts late and may never mount at all (its script 401s and
     retries); every read and write here tolerates an absent or half-built client rather than
     throwing inside a click handler and killing the button.
  --------------------------------------------------------------------------------------- */
  function primeForEdit() {
    try {
      var c = pp();
      if (!c || !c._vars) return '';
      if (c._vars.projectId) return c._vars.projectId;

      var id = currentProject();
      if (!id) return '';

      /* Put the session record back where the client expects to find it. Only when the archive
         actually holds it: inventing a record would be worse than letting PitchPrint decide. */
      var rec = store(PROJECTS)[id] || null;
      var productId = productIdFromRecord(rec) || c._vars.productId || '';
      if (rec && productId) {
        var sp = store(SP);
        if (projectIdOf(sp[productId]) !== id) {
          sp[productId] = rec;
          saveStore(SP, sp);
        }
      }

      c._vars.projectId = id;
      c._vars.mode = 'edit';
      return id;
    } catch (e) { return ''; }
  }

  function syncCard() {
    if (!card) return;
    var has = !!currentProject();
    /* A saved design IS the artwork choice, however the customer got here -- including arriving
       from a cart line's Edit link, where nothing was clicked on this page. Without this the line
       would go out saying "Upload Now or Email Later (Free)" with a design attached, which is the
       ambiguity Artwork status exists to remove. Mirrors applyLabel() on the online template. */
    if (has && radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (launch) {
      launch.hidden = !has;
      var l = has ? card.getAttribute('data-c4-launch-ready') : card.getAttribute('data-c4-launch-idle');
      if (l && launch.textContent !== l) launch.textContent = l;
    }
    if (remove) remove.hidden = !has;
    if (titleEl) {
      var tr = card.getAttribute('data-c4-title-ready');
      if (has && tr) titleEl.textContent = tr;
    }
    if (tagEl) {
      var gr = card.getAttribute('data-c4-tag-ready');
      if (has && gr) tagEl.textContent = gr;
    }
    syncCarouselVisibility();
  }

  /* Saved design -> the C4 gallery, through the shared slot c4-print-app.js reads. */
  /* WHY THE PREVIEW IS CACHE-BUSTED AFTER A SAVE (the "old design still shows" bug).
     PitchPrint rewrites the SAME file, previews/<projectId>_1.jpg, every time a project is saved.
     So a customer who edits or switches design and saves gets an identical URL, and the browser
     serves the image it already has -- the previous design. Nothing is late and no delay fixes it;
     the URL simply never changed. The Dawn build hit this too and worked around it with
     `?t=${Date.now()}` on its preview images. Here each save records a stamp for that project and
     the stamp rides on the URL, so the image is refetched after a save. A restored project has no
     stamp and keeps the clean URL.

     DO NOT "TIDY AWAY" THE ?v= PARAMETER. It is not decoration and it is not a version number
     anyone reads. It is the only thing that makes the browser ask PitchPrint for the picture
     again, because the filename never changes. Remove it and every re-edit silently shows the
     previous design. See refreshPreview() below for why one stamp per save was not enough. */
  var saveStamp = {};

  function publishPreview() {
    var c = pp();
    var id = currentProject();
    if (!slot) return;
    var url = id ? previewFor(id, 1) : '';
    if (url && saveStamp[id]) url += '?v=' + saveStamp[id];
    if (slot.value !== url) {
      slot.value = url;
      slot.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (c && c._vars) c._vars.mode = id ? 'edit' : 'new';
  }

  /* ---------------------------------------------------------------------------------------
     WHY ONE STAMP PER SAVE WAS NOT ENOUGH (the "re-edit still shows the old picture" report,
     14 Sep 2026). The cache-buster above was already here and is correct as far as it goes. The
     part that was wrong is WHEN it fired. The stamp was taken 150ms after 'project-saved' and the
     stamped URL was published immediately -- but PitchPrint regenerates previews/<projectId>_1.jpg
     on its own servers AFTER the save returns. So the one and only fetch happened before the new
     render existed, and the browser cached THE OLD PICTURE UNDER THE NEW KEY. From then on
     publishPreview() recomputed the identical URL on every poll tick, `slot.value !== url` was
     false, and nothing ever asked again: the stale render was pinned until the next save. The
     symptom therefore looked exactly like "no cache-busting at all", which is why it was reported
     that way -- but adding a cache-buster would have changed nothing.

     So the stamp is now taken SEVERAL TIMES over the ~20 seconds after a save, and each attempt
     is LOADED OFF-DOM FIRST and only published if it actually decodes:
       - a candidate that 404s (the very first save, before the file exists at all) is discarded
         and the picture already on screen is left alone, so the gallery never shows a broken
         thumbnail -- a visibly stale picture is much better than a missing one;
       - a candidate that loads replaces the slot value, and the gallery repaints from a URL the
         browser has already cached, so the swap is instant rather than a blank frame.
     Later attempts simply overwrite earlier ones, so whatever PitchPrint has finished generating
     by ~20s is what the customer ends up looking at. The cost is at most four requests for one
     small JPG per save.

     KNOWN, ACCEPTED SIDE EFFECT: each published change makes c4-print-app.js rebuild the artwork
     thumbnail, and syncArtwork() activates whichever thumbnail is new -- so the main gallery image
     jumps back to the design up to four times in those 20 seconds. Straight after a save that is
     what the customer wants to see anyway, and it is bounded. Fixing it properly would mean
     changing the activation rule in c4-print-app.js, which exists to solve a different bug.

     There is deliberately NO attempt to detect whether the regenerated image differs from the old
     one: both are valid JPGs of the same size from another origin, so nothing in the browser can
     tell them apart (canvas pixel reads are blocked cross-origin). Re-fetching on a short schedule
     is the honest version of the answer.
  --------------------------------------------------------------------------------------- */
  var REFRESH_AT = [0, 3000, 9000, 20000];
  var refreshTimers = [];

  function refreshPreview(id) {
    if (!id || !slot) return;
    var stamp = Date.now();
    var im = new Image();
    im.onload = function () {
      /* The customer may have cleared the design or switched project while this was in flight.
         Publishing then would resurrect a thumbnail for artwork that is no longer on the page. */
      if (currentProject() !== id) return;
      saveStamp[id] = stamp;
      publishPreview();
    };
    /* Not generated yet, or gone: keep whatever is on screen and let the next attempt try. */
    im.onerror = function () {};
    im.src = previewFor(id, 1) + '?v=' + stamp;
  }

  function schedulePreviewRefresh(id) {
    refreshTimers.forEach(function (t) { clearTimeout(t); });
    refreshTimers = [];
    if (!id) return;
    REFRESH_AT.forEach(function (ms) {
      refreshTimers.push(setTimeout(function () { refreshPreview(id); }, ms));
    });
  }

  /* ---------------------------------------------------------------------------------------
     3. TEMPLATE CAROUSEL. Native data only -- see DESIGN LIST at the top of this file.
  --------------------------------------------------------------------------------------- */
  var CACHE = 'c4pp-designs';
  /* Primary source is the draft theme's OWN assets/pp-designs.json. The fallback is the same file
     where it already lives on the current live theme, so the carousel works before the asset has
     been copied across and keeps working after -- whichever is present wins, no duplicate needed. */
  var DESIGNS_URL = (form && form.getAttribute('data-c4-pp-designs')) || '';
  var DESIGNS_FALLBACK = (form && form.getAttribute('data-c4-pp-designs-fallback')) || '';
  var PRODUCT_TITLE = (form && form.getAttribute('data-c4-product-title')) || '';
  /* CATALOGUE MODE (the Online Design Tool page, 12 Sep 2026): a form carrying data-c4-pp-all is
     not a print product, so instead of matching designs to the product title the carousel offers
     the whole file, one category at a time, with a row of category chips above the rail. */
  var ALL_MODE = !!(form && form.hasAttribute('data-c4-pp-all'));
  var category = '';  /* selected chip in catalogue mode */
  var catsEl = null;
  var all = [];       /* every record in the source, as loaded */
  var designs = [];   /* the records currently on screen, after filtering */
  var carousel = null, rail = null, countEl = null, emptyEl = null;
  var lastKey = null;
  var painted = false;        /* has the rail been drawn at least once */
  var carouselBootAt = Date.now();

  /* ---- the matching rules, carried over from the Dawn carousel ---------------------------- */
  function normText(v) { return String(v == null ? '' : v).toLowerCase().replace(/\s+/g, ''); }
  function normProduct(v) { v = normText(v); return v.slice(-1) === 's' ? v.slice(0, -1) : v; }
  /* "600 x 450mm", "600x450" and "Round (600x450mm)" all reduce to the same key. */
  function normShape(v) {
    return String(v == null ? '' : v).toLowerCase()
      .replace(/mm/g, '').replace(/[×x]/g, 'x').replace(/[^0-9x]/g, '').replace(/x+/g, 'x');
  }
  /* Design titles are ordered by a leading number ("0. Blank", "9. Banners"). */
  function leadNum(t) { var m = String(t || '').trim().match(/^(\d+)/); return m ? parseInt(m[1], 10) : 999999; }
  function titleOf(d) { return d.display_title || d.design_title || d.title || 'Template'; }
  function idOf(d) { return d.design_id || d.id || d._id || ''; }
  function previewOf(d) {
    var p = d.preview || '';
    return p.indexOf('http') === 0 ? p : previewFor(idOf(d), 1);
  }
  /* Category names are ordered for the admin's benefit, so they carry a numeric prefix that is not
     part of the product's name: the Novelty Cheques category is stored as "00. Novelty Cheques"
     and one Banners category as "9. Banners". Without stripping it, the product matched nothing and
     the carousel came up empty (found on Novelty Cheques, 10 Sep 2026). */
  function stripLead(v) { return String(v == null ? '' : v).replace(/^\s*\d+\s*[.)-]?\s*/, ''); }
  function rawCat(d) { return String(d.product || d.category || '').trim(); }
  function catOf(d) { return stripLead(rawCat(d)); }
  function productMatches(d, product) {
    var p = stripLead(d.product || d.category || '');
    var q = stripLead(product);
    return normText(p) === normText(q) || normProduct(p) === normProduct(q);
  }

  /* ---- reading the customer's Apippa selections -------------------------------------------
     Deliberately tolerant, in the style of c4-legacy.js: Apippa owns this DOM and renames things
     between calculator builds, so we look a field up by what it is CALLED (its data-identifier,
     its data-element, or its visible label) and then read whatever control is inside it. No
     assumptions about tag, type or position, and nothing is scraped outside #calculator. */
  function labelOf(node) {
    var n = node.getAttribute && (node.getAttribute('data-identifier') || node.getAttribute('data-element'));
    if (n) return n;
    var lab = node.querySelector && node.querySelector('label, legend, .element-label, .element-title');
    return lab ? lab.textContent : '';
  }
  function optionText(sel) {
    var o = sel.options && sel.options[sel.selectedIndex];
    if (!o) return sel.value || '';
    return o.getAttribute('data-label') || o.textContent || o.value || '';
  }
  function valueIn(node) {
    if (node.tagName === 'SELECT') return optionText(node).trim();
    if (node.tagName === 'INPUT') {
      if (node.type === 'radio' || node.type === 'checkbox') return node.checked ? (node.getAttribute('data-label') || node.value || '') : '';
      return (node.value || '').trim();
    }
    var sel = node.querySelector('select');
    if (sel && sel.selectedIndex > -1) { var t = optionText(sel).trim(); if (t) return t; }
    var chk = node.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
    if (chk) {
      var lbl = chk.getAttribute('data-label') || chk.value;
      if (!lbl && chk.id) { var l = node.querySelector('label[for="' + chk.id + '"]'); lbl = l && l.textContent; }
      if (lbl) return String(lbl).trim();
    }
    var txt = node.querySelector('input[type="text"], input[type="number"]');
    return txt && txt.value ? txt.value.trim() : '';
  }
  function apippaValue(names) {
    var calc = document.getElementById('calculator');
    if (!calc) return '';
    var wanted = names.map(normText);
    var nodes = calc.querySelectorAll('[data-identifier], [data-element], .element, .form-group');
    /* A product can carry the SAME field twice on purpose. End Sign Flag has one Shape section per
       End Flag option path, kept distinct in Apippa's admin by a trailing space -- confirmed by
       Apippa support, 14 Sep 2026, as intended rather than a mistake. Only one path is on screen at
       a time, and the customer's answer is the one they can see: the hidden copy keeps whatever it
       was last left with and would otherwise win simply by appearing first in the DOM. So prefer a
       visible match, and fall back to any match only when none is visible -- which is the case
       before the calculator has been laid out. */
    var fallback = '';
    for (var i = 0; i < nodes.length; i++) {
      if (wanted.indexOf(normText(labelOf(nodes[i]))) === -1) continue;
      var v = valueIn(nodes[i]);
      if (!v) continue;
      if (nodes[i].offsetParent !== null) return v;
      if (!fallback) fallback = v;
    }
    return fallback;
  }
  function selection() {
    return {
      size: apippaValue(['Size']),
      shape: apippaValue(['Shape']),
      orientation: apippaValue(['Orientation'])
    };
  }

  /* ---- filtering --------------------------------------------------------------------------
     Narrow by product, then by each Apippa selection. Each narrowing falls back to the records
     that do not specify that attribute, so a design marked "any size" survives a size choice. */
  /* Apippa calls its dimension field "Size", but the value it holds may be either of the JSON's
     two dimension fields: Banners' "Custom size" is recorded as a SHAPE, while Corflute's
     "Large 1400x600mm" is recorded as a SIZE. So a chosen value is tested against both. Values
     containing a digit compare as dimensions ("600 x 450mm" == "600x450"), the rest as text. */
  /* DIMENSIONS ARE WRITTEN IN WHATEVER UNIT THE AUTHOR FELT LIKE. Apippa hands us "2000 X 1000mm";
     the design library tags the very same banner "2m x 1m". normShape strips everything that is not
     a digit, so those two became "2000x1000" and "21" and could never match: a 2-metre banner found
     none of its 58 templates, fell through to the untagged pile, and offered an A5 sale sign that
     opened PitchPrint at a 21 x 14.8 cm canvas (Cathy, 14 Sep 2026). So parse both sides into
     millimetres and compare as numbers, with a small tolerance for rounding. The text comparison is
     kept as a fallback for tags that are words rather than measurements ("Flat Bottom"). */
  function parseDims(v) {
    var m = String(v == null ? '' : v).match(/(\d+(?:\.\d+)?)\s*(mm|cm|m)?\s*[x×]\s*(\d+(?:\.\d+)?)\s*(mm|cm|m)?/i);
    if (!m) return null;
    /* Unit from whichever side states one -- "2000 X 1000mm" only labels the second number. */
    var unit = String(m[4] || m[2] || 'mm').toLowerCase();
    var mult = unit === 'm' ? 1000 : (unit === 'cm' ? 10 : 1);
    var w = parseFloat(m[1]) * mult;
    var h = parseFloat(m[3]) * mult;
    if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;
    return [w, h];
  }
  function sameDims(a, b) {
    if (!a || !b) return false;
    var tol = Math.max(1, Math.max(a[0], a[1]) * 0.02);
    return Math.abs(a[0] - b[0]) <= tol && Math.abs(a[1] - b[1]) <= tol;
  }
  function isAnySize(d) {
    return String(d.shape || d.size || '').toLowerCase().indexOf('custom') === 0;
  }
  function dimensionMatches(d, value) {
    /* A template marked "Custom Size" scales to whatever the customer ordered, so it fits any size. */
    if (isAnySize(d)) return true;
    var want = parseDims(value);
    if (want && (sameDims(parseDims(d.size), want) || sameDims(parseDims(d.shape), want))) return true;
    var nt = normText(value);
    return (d.size && normText(d.size) === nt) || (d.shape && normText(d.shape) === nt);
  }
  /* What share of these designs carry a value for any of these fields. */
  function coverage(pool, keys) {
    if (!pool.length) return 0;
    var n = 0;
    pool.forEach(function (d) {
      for (var i = 0; i < keys.length; i++) { if (d[keys[i]]) { n++; return; } }
    });
    return n / pool.length;
  }

  function narrowDimension(pool, value) {
    if (!value) return pool;
    /* "Custom size" is the ABSENCE of a choice, not a measurement -- the customer has not committed
       to any preset yet. Matching it literally left Banners showing the two designs tagged
       "Custom Size" and hiding the other 83 (Cathy, 14 Sep 2026: "we should be seeing lots of
       designs"). Nothing is filtered away until a real size is chosen. */
    if (normText(value).indexOf('custom') === 0) return pool;
    var hit = pool.filter(function (d) { return dimensionMatches(d, value); });
    if (hit.length) return hit;
    /* Nothing recorded for that choice: prefer the designs that fit any size, and if there are
       none of those either, keep the product's full set rather than showing an empty rail. */
    var any = pool.filter(function (d) { return !d.size && !d.shape; });
    return any.length ? any : pool;
  }
  function narrowOrientation(pool, value) {
    if (!value) return pool;
    var hit = pool.filter(function (d) { return d.orientation && normText(d.orientation) === normText(value); });
    if (hit.length) return hit;
    var any = pool.filter(function (d) { return !d.orientation; });
    return any.length ? any : pool;
  }
  /* THE CATEGORY NAME IS THE AUTHORITY, not the record's product field. Cathy named each design
     category after the product deliberately, "to make sure similar named categories are not
     included in the carousel". Matching on the product field ignored that: the Banners product
     pulled in "Banners" (32), "9. Banners" (32, a duplicate registration of the same designs) and
     "Banners/Acp/Corflute/Panels" (21) -- the last being a spare category kept for odds and ends,
     which is where the A5 sale sign came from that PitchPrint then opened at a 21 x 14.8 cm canvas
     for a 2-metre banner (Cathy, 14 Sep 2026). All three carry product "Banners", so no test on
     the product field can tell them apart; only d.category can.

     d.category is read DIRECTLY rather than through catOf(), because catOf() deliberately prefers
     the product field and is used elsewhere -- the catalogue chips in paintCats() label themselves
     from it -- so it must keep that behaviour. stripLead() takes off the admin's ordering prefix
     ("00. Novelty Cheques", "9. Banners"), which is not part of the name. The product-field match
     is kept as a fallback for any product whose category is named differently -- without it, such
     a product would show nothing at all. */
  function categoryMatches(d, product) {
    return normProduct(stripLead(String(d.category == null ? '' : d.category))) === normProduct(product);
  }
  /* "Banners" and "9. Banners" are the same designs registered twice, so both survive the name
     match and every template appeared as an adjacent pair. Collapsing them is a guard, not the cure
     -- deleting the duplicate category in PitchPrint is -- but it keeps the rail honest meanwhile.

     THE KEY MUST CARRY EVERY DISTINGUISHING ATTRIBUTE, not just the title. Keyed on title alone it
     merged End Sign Flag's Flat Bottom and Triangular Bottom designs; keyed without orientation it
     merged Photo Prop Frames' portrait and landscape versions of the same design and left 6
     showing where there should have been about 18 (Cathy, 14 Sep 2026). Two records are duplicates
     only when the customer could not tell them apart. A record with no title is always kept rather
     than silently dropped. */
  function dedupeRecords(pool) {
    var seen = {}, out = [];
    pool.forEach(function (d) {
      var k = normText(titleOf(d)) + '|' + normText(d.shape) + '|' + normText(d.size) + '|' + normText(d.orientation);
      if (k && seen[k]) return;
      if (k) seen[k] = 1;
      out.push(d);
    });
    return out;
  }

  function filterFor(sel) {
    var pool;
    if (ALL_MODE) {
      pool = all.filter(function (d) { return !category || catOf(d) === category; });
    } else {
      pool = all.filter(function (d) { return categoryMatches(d, PRODUCT_TITLE); });
      if (!pool.length) pool = all.filter(function (d) { return productMatches(d, PRODUCT_TITLE); });
      pool = dedupeRecords(pool);
    }
    /* A product with no records of its own shows nothing rather than the whole catalogue: the
       Dawn version fell back to all 23 categories, which offered Corflute designs on a banner. */
    if (!pool.length) return [];
    if (ALL_MODE) {
      return pool.slice().sort(function (a, b) {
        return (leadNum(titleOf(a)) - leadNum(titleOf(b))) || String(titleOf(a)).localeCompare(String(titleOf(b)));
      });
    }

    /* WHICH AXIS MATTERS IS DECIDED BY THE DATA, NOT ASSUMED. A category only records the axis it
       actually varies on: Novelty Cheques records a size on nearly every design (three cheque sizes
       in one category), while Photo Prop Frames records orientation on 43 of 55 and leaves size
       blank on 47 -- those designs scale, so size is meaningless there and narrowing by it would
       drop good designs. So an axis is only applied when most of the product's designs carry a
       value for it. Nothing per-product is configured; add a category and it sorts itself out. */
    if (coverage(pool, ['size', 'shape']) >= 0.5) {
      pool = narrowDimension(pool, sel.size);
      pool = narrowDimension(pool, sel.shape);
    }
    if (coverage(pool, ['orientation']) >= 0.5) {
      pool = narrowOrientation(pool, sel.orientation);
    }
    return pool.slice().sort(function (a, b) {
      return (leadNum(titleOf(a)) - leadNum(titleOf(b))) || String(titleOf(a)).localeCompare(String(titleOf(b)));
    });
  }

  /* ---- the carousel ------------------------------------------------------------------------
     A plain horizontal scroller: overflow-x with scroll snapping, two arrows that page it, and
     nothing else. The Dawn version cloned items to fake an infinite loop, dragged with pointer
     maths, moved itself into .head_part_2 and blocked form submits while scrolling; none of that
     is carried over. Styles live in c4-pdp.css. */
  function buildCarousel() {
    if (!card || carousel) return;
    var host = card.parentNode;
    if (!host) return;

    carousel = document.createElement('div');
    carousel.className = 'c4-ppc';
    carousel.setAttribute('data-c4-pp-carousel', '');
    carousel.hidden = true;

    var head = document.createElement('div');
    head.className = 'c4-ppc__hd';
    var h = document.createElement('span');
    h.className = 'c4-ppc__title';
    h.textContent = 'Choose a free template';
    countEl = document.createElement('span');
    countEl.className = 'c4-ppc__count';
    head.appendChild(h); head.appendChild(countEl);

    var body = document.createElement('div');
    body.className = 'c4-ppc__body';
    rail = document.createElement('div');
    rail.className = 'c4-ppc__rail';
    rail.setAttribute('role', 'list');

    /* Tile clicks are DELEGATED to the rail rather than bound per tile, because the seamless loop
       (see paintCarousel) appends cloned tiles, and cloneNode does not copy listeners. Delegation
       means a clone behaves exactly like the tile it was cloned from. */
    rail.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var item = t.closest('.c4-ppc__item');
      if (!item || !rail.contains(item)) return;
      if (t.closest('.c4-ppc__go')) {
        e.preventDefault();
        e.stopPropagation();
        select(item);
        var nm = item.querySelector('.c4-ppc__name');
        chooseTemplate(item.getAttribute('data-design-id'), nm ? nm.textContent : '');
        return;
      }
      select(item);
    });

    /* The other half of the loop. Teleporting mid-animation would abort the smooth scroll, so the
       jump back from the clone set to the real set waits ~140ms after scrolling stops. The two sets
       are identical, so at the moment of the jump the visible tiles do not change at all. */
    var settle;
    rail.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(function () {
        var loop = loopWidth();
        if (loop && rail.scrollLeft >= loop) rail.scrollLeft = rail.scrollLeft - loop;
      }, 140);
    });

    body.appendChild(arrow('prev', '‹'));
    body.appendChild(rail);
    body.appendChild(arrow('next', '›'));

    emptyEl = document.createElement('p');
    emptyEl.className = 'c4-ppc__empty';
    emptyEl.hidden = true;
    emptyEl.textContent = 'No ready-made templates for this size. Customise starts you from a blank one.';

    carousel.appendChild(head);
    if (ALL_MODE) {
      catsEl = document.createElement('div');
      catsEl.className = 'c4-ppc__cats';
      catsEl.setAttribute('role', 'tablist');
      catsEl.setAttribute('aria-label', 'Template categories');
      catsEl.addEventListener('click', function (e) {
        var b = e.target && e.target.closest ? e.target.closest('[data-c4-pp-cat]') : null;
        if (!b) return;
        e.preventDefault();
        category = b.getAttribute('data-c4-pp-cat') || '';
        paintCats();
        refilter(true);
      });
      carousel.appendChild(catsEl);
    }
    carousel.appendChild(body);
    carousel.appendChild(emptyEl);
    host.appendChild(carousel);
  }

  /* Catalogue mode only: one chip per category, in the file's own numeric order. */
  function paintCats() {
    if (!ALL_MODE || !catsEl) return;
    var seen = {}, list = [];
    all.forEach(function (d) {
      var raw = rawCat(d), name = stripLead(raw);
      if (!name || seen[name]) return;
      seen[name] = true;
      list.push({ name: name, order: leadNum(raw) });
    });
    list.sort(function (a, b) { return (a.order - b.order) || a.name.localeCompare(b.name); });
    if (!category && list.length) category = list[0].name;
    catsEl.textContent = '';
    list.forEach(function (c) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'c4-ppc__cat' + (c.name === category ? ' is-on' : '');
      b.setAttribute('data-c4-pp-cat', c.name);
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', c.name === category ? 'true' : 'false');
      b.textContent = c.name;
      catsEl.appendChild(b);
    });
  }

  function arrow(dir, glyph) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'c4-ppc__arrow c4-ppc__arrow--' + dir;
    b.setAttribute('aria-label', dir === 'prev' ? 'Previous templates' : 'More templates');
    b.textContent = glyph;
    /* CONTINUOUS ROTATION. The first version wrapped by scrolling the whole way back to 0, which
       on an 85-design product is a long slide across every card -- reported 11 Sep as "when you get
       to the last card it slides back to the beginning instead of the next card to the right being
       the first". Now the rail holds the set twice (paintCarousel), so going past the last card
       simply scrolls one more step into the copy: the first card arrives from the right like any
       other. The teleport back to the real set happens once scrolling settles and is invisible,
       because both sets render identically. */
    b.addEventListener('click', function (e) {
      e.preventDefault();
      if (!rail) return;
      var step = Math.max(rail.clientWidth - 40, 160);
      var loop = loopWidth();

      if (!loop) {
        /* Too few designs to fill the rail, so there is no copy and nothing to rotate. Clamp. */
        var max = Math.max(rail.scrollWidth - rail.clientWidth, 0);
        rail.scrollTo({
          left: dir === 'prev' ? Math.max(rail.scrollLeft - step, 0) : Math.min(rail.scrollLeft + step, max),
          behavior: 'smooth'
        });
        return;
      }

      /* Going left off the front: hop forward one whole set first, at rest and instantly, so there
         is always room to scroll backwards into. */
      if (dir === 'prev' && rail.scrollLeft - step < 0) rail.scrollLeft = rail.scrollLeft + loop;
      rail.scrollTo({ left: rail.scrollLeft + (dir === 'prev' ? -step : step), behavior: 'smooth' });
    });
    return b;
  }

  /* Distance from the start of the rail to the first cloned tile -- i.e. the width of one full set
     of designs. Measured live rather than cached, because a tile whose preview 404s removes itself
     after paint. Returns 0 when there is no clone set, which is the "do not rotate" signal. */
  function loopWidth() {
    if (!rail) return 0;
    var firstClone = rail.querySelector('.c4-ppc__item.is-clone');
    return firstClone ? firstClone.offsetLeft : 0;
  }

  function paintCarousel() {
    if (!carousel || !rail) return;
    painted = true;
    rail.textContent = '';
    if (countEl) countEl.textContent = designs.length ? designs.length + (designs.length === 1 ? ' design' : ' designs') : '';
    if (emptyEl) emptyEl.hidden = !!designs.length;

    designs.forEach(function (d) {
      var id = idOf(d);
      if (!id) return;
      var item = document.createElement('div');
      item.className = 'c4-ppc__item';
      item.setAttribute('role', 'listitem');
      item.setAttribute('data-design-id', id);

      var wrap = document.createElement('div');
      wrap.className = 'c4-ppc__imgwrap';
      var img = document.createElement('img');
      img.className = 'c4-ppc__img';
      img.src = previewOf(d);
      img.alt = titleOf(d);
      img.loading = 'lazy';
      img.decoding = 'async';
      /* A design whose preview has been deleted upstream would otherwise show a broken tile. Both
         the tile and its clone go, so the two sets stay the same length. */
      img.addEventListener('error', function () {
        if (!rail) { item.remove(); return; }
        var same = rail.querySelectorAll('.c4-ppc__item');
        for (var i = same.length - 1; i >= 0; i--) {
          if (same[i].getAttribute('data-design-id') === id) same[i].remove();
        }
      });

      var go = document.createElement('button');
      go.type = 'button';
      go.className = 'c4-ppc__go';
      go.textContent = 'Customise';

      wrap.appendChild(img); wrap.appendChild(go);
      var name = document.createElement('span');
      name.className = 'c4-ppc__name';
      name.textContent = titleOf(d);
      item.appendChild(wrap); item.appendChild(name);
      rail.appendChild(item);
    });

    /* SEAMLESS LOOP. The set is appended a second time so that scrolling past the last card brings
       the first one in from the right, rather than sliding the whole rail back to the start. Only
       worth doing when the designs actually overflow the rail -- with two or three templates there
       is nothing to rotate, and the arrows clamp instead (see arrow()). Clones carry no listeners,
       which is why tile clicks are delegated on the rail in buildCarousel. */
    if (rail.scrollWidth > rail.clientWidth + 4) {
      var reals = rail.querySelectorAll('.c4-ppc__item');
      for (var i = 0; i < reals.length; i++) {
        var clone = reals[i].cloneNode(true);
        clone.classList.add('is-clone');
        clone.setAttribute('aria-hidden', 'true');
        rail.appendChild(clone);
      }
    }
    rail.scrollLeft = 0;
    syncCarouselVisibility();
  }

  /* Selection is matched by design id, not by element identity, so choosing a card highlights it in
     the real set and in the clone set together -- otherwise the highlight would vanish as the loop
     teleports between them. */
  function select(item) {
    if (!rail) return;
    var id = item && item.getAttribute ? item.getAttribute('data-design-id') : null;
    var kids = rail.querySelectorAll('.c4-ppc__item');
    for (var i = 0; i < kids.length; i++) {
      kids[i].classList.toggle('is-selected', id ? kids[i].getAttribute('data-design-id') === id : kids[i] === item);
    }
    chooseDesignRoute();
  }

  /* PICKING A DIFFERENT TEMPLATE WHILE A DESIGN ALREADY EXISTS.
     PitchPrint will not do this. Once _vars.projectId is set the client is in "edit" mode, and its
     Customise/Edit button reopens THAT PROJECT -- the design id we hand it is ignored. Verified
     10 Sep 2026: with a restored project, setting designId to a different template and clicking
     Customise reopened the saved artwork, mode still "edit". This is the long-standing "I picked a
     new design and the old one is still showing" report; it is not a timing problem and no delay
     fixes it. The only way to start from a different template is to clear the project first, which
     throws the customer's work away -- so we ask first, in the carousel, rather than silently. */
  var PENDING = 'c4pp-pending-design';

  /* Consumed once, on the load that follows a "Start again". Read-and-delete before opening, so a
     failure to open cannot leave the editor popping up on every future page view. */
  function resumePendingTemplate() {
    var id = null;
    try { id = sessionStorage.getItem(PENDING); sessionStorage.removeItem(PENDING); } catch (e) {}
    if (!id || currentProject()) return;
    openEditor(id);
  }

  function dropRestoreParam() {
    try {
      var u = new URL(window.location.href);
      if (!u.searchParams.has(RESTORE_PARAM)) return;
      u.searchParams.delete(RESTORE_PARAM);
      u.searchParams.delete(LINE_PARAM);
      window.history.replaceState({}, '', u.pathname + (u.search || '') + u.hash);
    } catch (e) {}
  }

  function chooseTemplate(id, title) {
    if (!currentProject()) { openEditor(id); return; }
    askBeforeSwitch(id, title);
  }

  function askBeforeSwitch(id, title) {
    if (!carousel) return;
    var bar = carousel.querySelector('[data-c4-ppc-confirm]');
    if (bar) bar.remove();
    bar = document.createElement('div');
    bar.className = 'c4-ppc__confirm';
    bar.setAttribute('data-c4-ppc-confirm', '');

    var msg = document.createElement('p');
    msg.className = 'c4-ppc__confirm-msg';
    msg.textContent = 'Start again from "' + (title || 'this template') + '"? Your current design will be discarded.';

    var go = document.createElement('button');
    go.type = 'button';
    go.className = 'c4-ppc__confirm-go';
    go.textContent = 'Start again';
    go.addEventListener('click', function () { bar.remove(); startOver(id); });

    var keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'c4-ppc__confirm-keep';
    keep.textContent = 'Keep my design';
    keep.addEventListener('click', function () { bar.remove(); });

    bar.appendChild(msg); bar.appendChild(go); bar.appendChild(keep);
    carousel.appendChild(bar);
    go.focus();
  }

  /* Clear through PitchPrint's own button so its state machine stays in charge, wait for the
     project to actually go, then open the chosen template as a new design. */
  function startOver(id) {
    /* Drop ?pp_project= first. PitchPrint's Clear reloads the page, and while that parameter is
       still in the URL our own restore would put the cleared design straight back -- so the
       customer would clear, switch, and find the old artwork again. Seen 10 Sep 2026. */
    dropRestoreParam();
    /* PitchPrint's Clear reloads the page, which loses the template the customer just chose -- so
       they would land on a blank product page having asked to start again from a specific design,
       which reads as nothing having happened. Park the choice and pick it up after the reload. */
    try { sessionStorage.setItem(PENDING, id); } catch (e) {}
    clickPP('pp_clear_design_btn');
    if (slot) slot.value = '';
    var tries = 0;
    (function waitCleared() {
      if (!currentProject() || tries++ > 20) {
        syncCard();
        openEditor(id);
        return;
      }
      setTimeout(waitCleared, 150);
    })();
  }

  /* Visible on the design route whenever there are templates to show. It deliberately stays up
     after a design is saved, so switching template is possible at all -- see chooseTemplate. */
  function syncCarouselVisibility() {
    if (!carousel) return;
    var wanted = !!designs.length && !!(radio && radio.checked);
    if (carousel.hidden === wanted) carousel.hidden = !wanted;
  }

  function calcMounted() {
    var c = document.getElementById('calculator');
    return !!(c && c.children.length);
  }
  function refilter(force) {
    if (!all.length) return;
    /* WAIT FOR APIPPA BEFORE THE FIRST PAINT. The design list is cached and ready long before the
       calculator mounts, so the first pass read every axis as empty, narrowed nothing, and painted
       the product's whole set -- then repainted seconds later once the size resolved. On Novelty
       Cheques that is 69 designs collapsing to 23 in front of the customer (Cathy, 14 Sep 2026:
       "the right design isn't presenting. its like a bit of a delay"). Deliberately placed BEFORE
       the key comparison below so the skipped pass does not record a key and block the real one.
       The deadline keeps a product whose calculator never arrives from showing an empty rail. */
    if (!painted && !calcMounted() && Date.now() - carouselBootAt < 8000) return;
    var sel = selection();
    if (ALL_MODE) paintCats();
    /* normText, NOT normShape. normShape is the DIMENSION normaliser -- it strips everything that
       is not a digit so that "600 x 450mm" compares equal to "600x450". Run over a TEXTUAL shape
       like "Flat Bottom" it returns an empty string, so this key came out identical whichever shape
       was selected, and refilter() bailed at the equality check below. End Sign Flag therefore never
       re-filtered when the customer switched between Flat Bottom and Triangular Bottom, and showed
       all 14 designs instead of 7 (Cathy, 14 Sep 2026). Products filtered by SIZE were unaffected,
       which is why this went unnoticed. The key only has to detect change; normText preserves both
       dimensional and textual values. */
    var key = normProduct(PRODUCT_TITLE) + '|' + normText(category) + '|' + normText(sel.size) + '|' + normText(sel.shape) + '|' + normText(sel.orientation);
    if (!force && key === lastKey) return;
    lastKey = key;
    designs = filterFor(sel);
    paintCarousel();
  }

  function useList(list) {
    if (!Array.isArray(list) || !list.length) return false;
    all = list;
    refilter(true);
    return true;
  }

  function cachedDesigns() {
    try { var v = JSON.parse(sessionStorage.getItem(CACHE) || 'null'); return Array.isArray(v) ? v : []; }
    catch (e) { return []; }
  }
  function cacheDesigns(list) {
    /* Only the records for THIS product go in the cache -- the full file is far too big for
       sessionStorage and there is no reason to carry another product's designs around. */
    try { sessionStorage.setItem(CACHE, JSON.stringify(list)); } catch (e) {}
  }

  /* Native first: if PitchPrint ever hands the parent a design list, it wins over the file. */
  function readNativeDesigns() {
    var c = pp();
    if (!c || !c._vars) return [];
    var raw = c._vars.designList;
    if ((!raw || !raw.length) && c._vars.designStack) {
      raw = Object.keys(c._vars.designStack).map(function (k) { return c._vars.designStack[k]; });
    }
    return (raw || []).map(function (d) {
      return { design_id: d.id || d._id, display_title: d.title || d.name, product: PRODUCT_TITLE };
    }).filter(function (d) { return !!d.design_id; });
  }

  var loadStarted = false;
  function loadDesigns() {
    if (loadStarted) return;
    loadStarted = true;
    if (!ALL_MODE && useList(readNativeDesigns())) return;

    var cached = ALL_MODE ? [] : cachedDesigns();
    if (cached.length) { all = cached; refilter(true); }

    var sources = [DESIGNS_URL, DESIGNS_FALLBACK].filter(Boolean);
    (function next(i) {
      if (i >= sources.length) return;
      fetch(sources[i], { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var list = data && (data.designs || data.data || data);
          if (!Array.isArray(list) || !list.length) { next(i + 1); return; }
          all = list;
          refilter(true);
          if (!ALL_MODE) cacheDesigns(all.filter(function (d) { return productMatches(d, PRODUCT_TITLE); }));
        })
        .catch(function () { next(i + 1); });
    })(0);
  }

  /* Re-filter when the customer changes a calculator field. Scoped to #calculator -- no observer
     on document.body, and no listener that can see or cancel a form submit. */
  function watchCalculator() {
    var calc = document.getElementById('calculator');
    if (!calc || calc.hasAttribute('data-c4-ppc-watched')) return;
    calc.setAttribute('data-c4-ppc-watched', '');
    ['change', 'input'].forEach(function (evt) {
      calc.addEventListener(evt, function () { setTimeout(function () { refilter(false); }, 60); });
    });
  }

  /* ------------------------------------------------------------------------------------- */
  function wire() {
    var c = pp();
    if (!c || !c.on) return false;

    c.on('project-saved', function () {
      chooseDesignRoute();
      /* PitchPrint fills its own hidden inputs and its product slot first; read after it settles,
         and archive straight away so the record survives the release after add-to-cart. */
      setTimeout(function () {
        /* Start re-fetching the preview. PitchPrint has not finished regenerating the JPG yet, so
           the picture is chased over the next ~20s rather than stamped once here -- see
           refreshPreview(). publishPreview() below still runs immediately so the slot carries this
           project at all; the schedule only replaces the picture as better renders arrive. */
        var id = currentProject();
        schedulePreviewRefresh(id);
        archiveCurrent(); publishPreview(); syncCard();
      }, 150);
    });
    c.on('session-saved', function () { setTimeout(archiveCurrent, 150); });

    if (browse) browse.addEventListener('click', function (e) { e.preventDefault(); openEditor(null); });
    /* See primeForEdit(). The project is handed back to PitchPrint immediately before its own
       Edit button is clicked, so the click cannot fall through to "start a new design".

       THE STICKY designId TRAP. openEditor() writes c._vars.designId when the customer picks a
       template from the carousel and NOTHING ever clears it. So a client that has lost its project
       does not open empty -- it opens that template, which is exactly the "my edits are gone, I am
       back at the original" report. It is STASHED AND RESTORED rather than cleared outright,
       because _vars.designId is PitchPrint's field, not ours: its own category gallery and its
       #pp_design_select read it, and a permanently blank designId could leave the customer unable
       to start a new design later. Blanking it only across the click removes it from whatever
       payload the Edit press builds, then puts it back a beat later -- and only if PitchPrint has
       not set one of its own in the meantime. This is belt and braces: setting projectId and mode
       above is the load-bearing half. */
    if (launch) launch.addEventListener('click', function (e) {
      e.preventDefault();
      chooseDesignRoute();
      var id = primeForEdit();
      var c = pp();
      var stash = '';
      if (id && c && c._vars && c._vars.designId) {
        stash = c._vars.designId;
        c._vars.designId = '';
      }
      clickPP('pp_edit_btn');
      if (stash) setTimeout(function () {
        try { if (c._vars && !c._vars.designId) c._vars.designId = stash; } catch (err) {}
      }, 1200);
    });

    /* Clear Design keeps the card's own confirm dialog (c4-artwork-design-card.liquid): the
       dialog is shared with the online template, only the actuator differs. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-c4-clear-go]')) return;
      setTimeout(function () {
        clickPP('pp_clear_design_btn');
        if (slot) slot.value = '';
        syncCard();
      }, 0);
    });

    /* The card is the only artwork control the customer sees, so choosing it should offer the
       templates rather than sit inert. */
    if (card) card.addEventListener('click', function (e) {
      if (e.target.closest('[data-c4-design-launch], [data-c4-design-remove], [data-c4-design-browse]')) return;
      chooseDesignRoute();
      syncCard();
    });

    buildCarousel();
    watchCalculator();
    loadDesigns();
    publishPreview();
    syncCard();
    resumePendingTemplate();

    /* PitchPrint mounts asynchronously and re-renders its buttons; a slow poll keeps the card in
       step without a body-wide observer. Backs off once things settle, idle while hidden. */
    var wait = 600, calm = 0, last = '';
    (function tick() {
      setTimeout(function () {
        if (!document.hidden) {
          publishPreview();
          syncCard();
          /* Apippa mounts after us and re-renders its own fields, so keep looking for the
             calculator until it appears, then keep the filter in step with it. */
          watchCalculator();
          if (!all.length) useList(readNativeDesigns());
          refilter(false);
          var sig = currentProject() + '|' + designs.length + '|' + lastKey;
          if (sig === last) { calm++; if (calm >= 4) wait = Math.min(wait * 2, 6000); }
          else { last = sig; calm = 0; wait = 600; }
        }
        tick();
      }, wait);
    })();
    return true;
  }

  /* THE CAROUSEL DOES NOT WAIT FOR PITCHPRINT. Its list comes from the theme's own JSON and its
     filtering from Apippa; only launching the editor needs ppclient. Building it here means a slow
     or failing PitchPrint (their script 401s and retries often enough) cannot cost the customer the
     template strip -- which is exactly what happened before this was split out: wire() gave up
     after 16s of waiting for ppclient and nothing was ever built. */
  if (onProduct) {
    buildCarousel();
    watchCalculator();
    loadDesigns();
    syncCard();
    /* SCROLL THE CAROUSEL INTO VIEW WHEN THE CUSTOMER PICKS THE DESIGN TILE (Cathy, 15 Sep 2026:
       "If a customer then selects it can you make it scroll down a little to be able to see the
       carousel?"). Only on a real click (isTrusted) -- programmatic re-checks after a restore must
       not move the page. The carousel is shown at once rather than waiting for the 1.5 s poll,
       then scrolled with block:'nearest' so the page moves only as far as it has to. */
    if (radio) {
      radio.addEventListener('change', function (e) {
        if (!e.isTrusted || !radio.checked) return;
        syncCarouselVisibility();
        setTimeout(function () {
          if (!carousel || carousel.hidden) return;
          try { carousel.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (err) { carousel.scrollIntoView(); }
        }, 80);
      });
    }
    setInterval(function () {
      if (document.hidden) return;
      watchCalculator();
      refilter(false);
      syncCarouselVisibility();
    }, 1500);
  }

  /* PitchPrint's client can take a long time to appear, so keep offering to wire up for two
     minutes rather than sixteen seconds, then stop. */
  if (!wire()) {
    var tries = 0;
    var t = setInterval(function () {
      if (wire() || ++tries > 300) clearInterval(t);
    }, 400);
  }
})();
